import { Box, Button, Grid } from '@mui/material'
import React, { useEffect, useState } from 'react'
import EmptyTableRow from 'src/Component/EmptyTableRow'
import PaginatedTable, { HeadCellI, SortOrderI } from 'src/Component/PaginatedTable'
import DataRow from './DataRow'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'src/store'
import { startPageLoader, stopPageLoader } from 'src/store/features/common/commonSlice'
import { errorToast, successToast } from 'src/utils/toastService'
import SearchInput from 'src/Component/SearchInput'
import { useDeleteParkMutation, useGetParksListQuery } from 'src/api/manageParksApi'
import AddParkModal from './AddParkModal'
import ConfirmationBox from 'src/Component/ConfirmationBox'
import ViewParkDetailsModal from './ViewParkDetailsModal'
import EditParkModal from './EditParkModal'

const headCells: HeadCellI[] = [
  {
    id: 'id',
    isCheckBox: true,
    sortable: false,
    label: 'checkbox',
  },
  {
    id: 'name',
    sortable: true,
    label: 'Park Name',
  },
  {
    id: 'address',
    sortable: true,
    label: 'Address',
  },
  {
    id: 'latitude',
    sortable: true,
    label: 'Latitude',
  },
  {
    id: 'longitude',
    sortable: true,
    label: 'Longitude',
  },
  {
    id: 'id',
    sortable: false,
    label: 'Action',
    align: 'center',
  },
]

const ManageParks = () => {
  const [page, setPage] = useState<number>(1)
  const [sortOrder, setSortOrder] = useState<SortOrderI>('asc')
  const [sortOrderBy, setSortOrderBy] = useState<string>('')
  const [openAddNewModal, setOpenAddNewModal] = useState(false)
  const [parkDetailsModal, setParkDetailsModal] = useState({
    id: 0,
    open: false,
  })

  const [parkDeleteConfirmation, setParkDeleteConfirmation] = useState({
    id: 0,
    open: false,
  })

  const [editParkModal, setEditParkModal] = useState({
    id: 0,
    open: false,
  })

  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
  const [search, setSearch] = useState<string>('')

  // hooks
  const {
    data: PARKS_DATA_RESPONSE,
    isFetching,
    refetch,
  } = useGetParksListQuery(
    {
      search,
      page,
      pageSize: 10,
      sortBy: (() => {
        if (sortOrder === 'asc') {
          return sortOrderBy
        } else {
          return `-${sortOrderBy}`
        }
      })(),
    },
    { refetchOnMountOrArgChange: true },
  )
  const [deletePark, { isLoading }] = useDeleteParkMutation()
  const dispatch = useDispatch<AppDispatch>()

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handleSorting = (_sortOrderBy: string) => {
    setSortOrder((prevState) => {
      return prevState === 'asc' ? 'desc' : 'asc'
    })
    setSortOrderBy(_sortOrderBy)
  }

  const handleParkDetailsModalClose = () => {
    setParkDetailsModal({
      id: 0,
      open: false,
    })
  }

  const handleViewDetailsClick = (id: number) => {
    setParkDetailsModal({
      id: id,
      open: true,
    })
  }

  const handleCloseDeleteConfirmation = () => {
    setParkDeleteConfirmation({
      id: 0,
      open: false,
    })
  }

  const handleOpenDeleteConfirmation = (id: number) => {
    setParkDeleteConfirmation({
      id: id,
      open: true,
    })
  }

  const handleEditParkModalClose = () => {
    setEditParkModal({
      id: 0,
      open: false,
    })
  }

  const handleEditParkClick = (id: number) => {
    setEditParkModal({
      id: id,
      open: true,
    })
  }

  const handleDeletePark = () => {
    deletePark(parkDeleteConfirmation.id)
      .unwrap()
      .then((res) => {
        console.log(res)
        successToast('Park deleted successfully.')
        handleEditParkModalClose()
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to delete park.')
      })
      .finally(() => {
        handleCloseDeleteConfirmation()
      })
  }

  const handleSelectUser = (id: number) => {
    if (selectedUserIds.includes(id)) {
      setSelectedUserIds((prevState) => {
        return prevState.filter((userId) => userId !== id)
      })
    } else {
      setSelectedUserIds((prevState) => {
        return [...prevState, id]
      })
    }
  }

  const handleSelectAllClick = (checked: boolean) => {
    if (checked && PARKS_DATA_RESPONSE?.results.length) {
      setSelectedUserIds(PARKS_DATA_RESPONSE.results.map((user) => user.id))
    } else {
      setSelectedUserIds([])
    }
  }

  const handleDeleteSelected = () => {
    Promise.all(selectedUserIds.map((id) => deletePark(id).unwrap()))
      .then((res) => {
        console.log(res)
        successToast('Selected parks deleted successfully.')
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to delete selected parks.')
      })
  }

  const handleSearch = (value: string) => {
    setPage(1)
    setSearch(value)
  }

  const onAddParkSuccessCallback = () => {
    setOpenAddNewModal(false)
    refetch()
  }

  const onEditParkSuccessCallback = () => {
    handleEditParkModalClose()
    refetch()
  }

  // use effects
  useEffect(() => {
    if (isFetching || isLoading) {
      dispatch(startPageLoader())
    } else {
      dispatch(stopPageLoader())
    }
  }, [isFetching, isLoading])

  useEffect(() => {
    setSelectedUserIds([])
  }, [PARKS_DATA_RESPONSE])

  return (
    <Box className='pageContainer'>
      <Grid container>
        <Grid md={8} sm={12} xs={12}>
          {' '}
        </Grid>
        <Grid md={4} sm={12} xs={12}>
          <Box>
            <SearchInput hasDebounce onChange={handleSearch} />
          </Box>
        </Grid>
      </Grid>

      <Box mt={10} textAlign='right'>
        <Button
          variant='contained'
          color='primary'
          type='button'
          onClick={() => setOpenAddNewModal(true)}
        >
          ADD NEW
        </Button>
      </Box>

      <Box mt={2}>
        {PARKS_DATA_RESPONSE && (
          <PaginatedTable
            title='Manage Parks'
            headCells={headCells}
            currentPage={page}
            onPageChange={handlePageChange}
            totalPages={Math.ceil(PARKS_DATA_RESPONSE?.count / 10)}
            onSortClick={handleSorting}
            onSelectAllClick={handleSelectAllClick}
            isAllSelected={PARKS_DATA_RESPONSE.results.length === selectedUserIds.length}
            numSelected={selectedUserIds.length}
            onDeleteSelected={handleDeleteSelected}
          >
            <>
              {PARKS_DATA_RESPONSE?.results?.length === 0 && (
                <EmptyTableRow cellCount={headCells.length} />
              )}
              {PARKS_DATA_RESPONSE?.results?.map((user) => {
                return (
                  <DataRow
                    key={user.id}
                    id={user.id}
                    name={user.name}
                    address={user.address}
                    latitude={user.latitude}
                    longitude={user.longitude}
                    selected={selectedUserIds.includes(user.id)}
                    onViewDetailsClick={handleViewDetailsClick}
                    onDeleteClick={handleOpenDeleteConfirmation}
                    onSelect={handleSelectUser}
                    onEditClick={handleEditParkClick}
                  />
                )
              })}
            </>
          </PaginatedTable>
        )}
      </Box>

      <AddParkModal
        open={openAddNewModal}
        onClose={() => setOpenAddNewModal(false)}
        successCallback={onAddParkSuccessCallback}
      />
      {!!parkDetailsModal.id && (
        <ViewParkDetailsModal
          id={parkDetailsModal.id}
          open={parkDetailsModal.open}
          onClose={handleParkDetailsModalClose}
        />
      )}

      <ConfirmationBox
        title='Are you sure you want to Delete this park.'
        open={parkDeleteConfirmation.open}
        onReject={handleCloseDeleteConfirmation}
        onAccept={handleDeletePark}
      />

      {!!editParkModal.id && (
        <EditParkModal
          id={editParkModal.id}
          open={editParkModal.open}
          onClose={handleEditParkModalClose}
          successCallback={onEditParkSuccessCallback}
          onDeleteClick={handleOpenDeleteConfirmation}
        />
      )}
    </Box>
  )
}

export default ManageParks
